/**
 * Educational yield model for $SI fee → holder airdrops.
 * Not financial advice — projections only.
 */

export const DEFAULTS = {
  creatorFeePct: 1.5, // ~1.5% creator share on 2% standard tier
  lotteryPct: 10,
  ataDragPct: 15, // estimated ATA rent + priority fees as % of post-lottery fees
  retainPct: 40, // matches leaving (100 − DIST_PCT) for reserve / later cycles
  volume24hUsd: 250_000,
  priceUsd: 0.00023,
  marketCapUsd: 230_000,
  supplyUi: 1_000_000_000, // fallback if mcap/price unknown
  myTokens: 100_000,
  eligibleHolders: 1000,
};

/**
 * @param {object} input
 */
export function projectYield(input = {}) {
  const creatorFeePct = clamp(num(input.creatorFeePct, DEFAULTS.creatorFeePct), 0, 5);
  const lotteryPct = clamp(num(input.lotteryPct, DEFAULTS.lotteryPct), 0, 50);
  const ataDragPct = clamp(num(input.ataDragPct, DEFAULTS.ataDragPct), 0, 50);
  const retainPct = clamp(num(input.retainPct, DEFAULTS.retainPct), 0, 90);
  const volume24hUsd = Math.max(0, num(input.volume24hUsd, DEFAULTS.volume24hUsd));
  const priceUsd = Math.max(0, num(input.priceUsd, DEFAULTS.priceUsd));
  let supplyUi = Math.max(0, num(input.supplyUi, 0));
  const marketCapUsd = Math.max(0, num(input.marketCapUsd, 0));
  if (supplyUi <= 0 && priceUsd > 0 && marketCapUsd > 0) {
    supplyUi = marketCapUsd / priceUsd;
  }
  if (supplyUi <= 0) supplyUi = DEFAULTS.supplyUi;
  const myTokens = Math.max(0, num(input.myTokens, DEFAULTS.myTokens));
  const eligibleHolders = Math.max(1, Math.floor(num(input.eligibleHolders, DEFAULTS.eligibleHolders)));

  const grossFeesUsd = volume24hUsd * (creatorFeePct / 100);
  const lotteryUsd = grossFeesUsd * (lotteryPct / 100);
  const afterLottery = grossFeesUsd - lotteryUsd;
  const ataUsd = afterLottery * (ataDragPct / 100);
  const afterAta = afterLottery - ataUsd;
  const retainUsd = afterAta * (retainPct / 100);
  const holderPoolUsd = Math.max(0, afterAta - retainUsd);

  const share = supplyUi > 0 ? myTokens / supplyUi : 0;
  const myDailyUsd = holderPoolUsd * share;
  const myBagUsd = myTokens * priceUsd;
  const dailyYieldPct = myBagUsd > 0 ? (myDailyUsd / myBagUsd) * 100 : 0;
  const apyPct = dailyYieldPct * 365;

  // Equal-odds lottery EV (not pro-rata)
  const lotteryEvDailyUsd = lotteryUsd / eligibleHolders;
  const lotteryEvApyPct = myBagUsd > 0 ? (lotteryEvDailyUsd / myBagUsd) * 365 * 100 : 0;

  return {
    inputs: {
      creatorFeePct,
      lotteryPct,
      ataDragPct,
      retainPct,
      volume24hUsd,
      priceUsd,
      marketCapUsd: marketCapUsd || priceUsd * supplyUi,
      supplyUi,
      myTokens,
      eligibleHolders,
    },
    grossFeesUsd,
    lotteryUsd,
    ataUsd,
    retainUsd,
    holderPoolUsd,
    sharePct: share * 100,
    myDailyUsd,
    myBagUsd,
    dailyYieldPct,
    apyPct,
    lotteryEvDailyUsd,
    lotteryEvApyPct,
    combinedDailyUsd: myDailyUsd + lotteryEvDailyUsd,
    combinedApyPct:
      myBagUsd > 0 ? ((myDailyUsd + lotteryEvDailyUsd) / myBagUsd) * 365 * 100 : 0,
  };
}

function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function clamp(n, lo, hi) {
  return Math.min(hi, Math.max(lo, n));
}

export function fmtUsd(n, digits = 2) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const v = Number(n);
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (abs >= 10_000) return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (abs >= 1) return `$${v.toLocaleString(undefined, { maximumFractionDigits: digits })}`;
  if (abs >= 0.01) return `$${v.toFixed(digits)}`;
  if (abs > 0) return `$${v.toFixed(4)}`;
  return "$0";
}

export function fmtPct(n, digits = 1) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const v = Number(n);
  if (Math.abs(v) >= 1000) return `${v.toFixed(0)}%`;
  if (Math.abs(v) >= 10) return `${v.toFixed(digits)}%`;
  return `${v.toFixed(Math.max(digits, 2))}%`;
}

/** StonkFun public token endpoint → calculator seeds */
export async function fetchSiMarket(mint) {
  if (!mint) return null;
  const res = await fetch(`https://www.stonkfun.xyz/api/public/v1/tokens/${mint}`, {
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`StonkFun HTTP ${res.status}`);
  const body = await res.json();
  const token = body?.data?.token || body?.token || body;
  const market = token?.market || {};
  return {
    volume24hUsd: Number(market.volume24hUsd) || 0,
    priceUsd: Number(market.priceUsd) || 0,
    marketCapUsd: Number(market.marketCapUsd) || 0,
    symbol: token?.symbol || "SI",
    name: token?.name || null,
  };
}
